import {nodeColumn} from '@sollapay/ui/components';

import {getBalanceAmount} from '../../utils';
import {TrustAccountBalanceChip} from '../TrustAccountBalanceChip';
import {TrustAccountNameCell} from '../TrustAccountNameCell';
import {TrustAccountRowAction} from '../TrustAccountRowAction';
import {TrustAccountTypeCell} from '../TrustAccountTypeCell';

import type {TrustAccountListItem} from '../../utils';
import type {ColumnWidth, TableProps} from '@sollapay/ui/components';
import type {ReactNode} from 'react';

// Column ids match the sort config's fields, so a header click and the sort select share one state.

export type TrustAccountListColumn<T> = TableProps<T, unknown>['columns'][number];

/** Every list passes its own widths, so its tables (and skeleton) share one column layout. */
interface ColumnOptions {
  header: string;
  width?: ColumnWidth;
}

export function trustAccountNameColumn<T extends TrustAccountListItem>(
  options: ColumnOptions & {renderRoleBadge?: (row: T) => ReactNode}
): TrustAccountListColumn<T> {
  return nodeColumn<T>({
    id: 'trustName',
    header: options.header,
    width: options.width,
    accessorFn: row => row.name,
    cell: row => (
      <TrustAccountNameCell
        variant="compact"
        name={row.name}
        refId={row.refId}
        trustPurpose={row.trustPurpose}
        rowLinkTo={row.path}
        roleBadge={options.renderRoleBadge?.(row)}
      />
    )
  });
}

export function trustAccountBalanceColumn<T extends TrustAccountListItem>(
  options: ColumnOptions
): TrustAccountListColumn<T> {
  return nodeColumn<T>({
    id: 'balance',
    header: options.header,
    width: options.width,
    accessorFn: row => getBalanceAmount(row.trustStatus, row.balance) ?? 0,
    cell: row => <TrustAccountBalanceChip status={row.trustStatus} balance={row.balance} />
  });
}

export function trustAccountTypeColumn<T extends TrustAccountListItem>(
  options: ColumnOptions
): TrustAccountListColumn<T> {
  return nodeColumn<T>({
    id: 'type',
    header: options.header,
    width: options.width,
    accessorFn: row => row.trustPurpose ?? '',
    cell: row => (
      <TrustAccountTypeCell
        trustPurpose={row.trustPurpose}
        generalSaleTransactionType={row.generalSaleTransactionType}
      />
    )
  });
}

/** The date, swapped for the row's call to action on hover, with the row's menu pinned beside it. */
export function trustAccountDateActionColumn<T extends TrustAccountListItem>(
  options: ColumnOptions & {
    id: string;
    getCtaLabel: (row: T) => string;
    onAction: (row: T) => void;
    renderMenu?: (row: T) => ReactNode;
  }
): TrustAccountListColumn<T> {
  return nodeColumn<T>({
    id: options.id,
    header: options.header,
    width: options.width,
    accessorFn: row => new Date(row.date).getTime(),
    cell: row => (
      <TrustAccountRowAction
        date={row.date}
        ctaLabel={options.getCtaLabel(row)}
        onAction={() => options.onAction(row)}
        menu={options.renderMenu?.(row)}
      />
    )
  });
}
